import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../../types';
import { avatarColor, initialOf } from '../../common/avatar';
import { updateClientComment } from '../CustomerList/helpers';
import style from './ClientDetailsModal.module.css';
import ExitLogo from './icons/exitLogo';
import DeleteLogo from '../ModalWindow/icons/DeleteLogo';
import SectionHeading, {
  PersonIcon,
  StarIcon,
} from '../../common/SectionHeading/SectionHeading';

interface ClientDetailsModalProps {
  client: Client;
  onClose: () => void;
  onDelete?: () => void;
  onCommentSaved?: (clientId: string, comment: string) => void;
}

// Договір недійсний, якщо дата закінчення в минулому → показуємо номер червоним.
const isContractExpired = (finishAt?: string | null): boolean => {
  if (!finishAt) return false;
  const finish = new Date(finishAt).getTime();
  return !Number.isNaN(finish) && finish < Date.now();
};

const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  client,
  onClose,
  onDelete,
  onCommentSaved,
}) => {
  const formattedCreatedAt = client.createdAt
    ? dayjs(client.createdAt).format('DD.MM.YYYY')
    : null;
  const formattedLastLoginAt = client.lastLoginAt
    ? dayjs(client.lastLoginAt).format('DD.MM.YYYY HH:mm')
    : null;

  const navigate = useNavigate();
  const [comment, setComment] = useState<string>(client.comment || '');
  const [isEditingComment, setIsEditingComment] = useState<boolean>(false);
  const [savingComment, setSavingComment] = useState<boolean>(false);
  const [draftComment, setDraftComment] = useState<string>(comment);

  // Тариф: для співробітника компанії — № договору (з рахунку власника), інакше «Без договору».
  const hasCompany = !!(client.companyName && client.companyOwnerId);
  const contractExpired = isContractExpired(client.contractFinishAt);
  const goToCompany = () => {
    if (!client.companyOwnerId) return;
    onClose();
    navigate(`/company/${client.companyOwnerId}`);
  };

  const startEditComment = () => {
    setDraftComment(comment);
    setIsEditingComment(true);
  };

  const cancelEditComment = () => {
    setIsEditingComment(false);
    setDraftComment(comment);
  };

  const saveComment = async () => {
    const next = draftComment.trim();
    setSavingComment(true);
    try {
      await updateClientComment(client.id, next);
      setComment(next);
      setIsEditingComment(false);
      onCommentSaved?.(client.id, next);
    } catch (error) {
      console.error('Не вдалося зберегти коментар:', error);
    } finally {
      setSavingComment(false);
    }
  };

  return (
    <div className={style.modalOverlay}>
      <div className={style.modalContent}>
        <div className={style.header}>
          <h5>Клієнт: {client.userName}</h5>
          <button onClick={onClose} className={style.closeButton}>
            <ExitLogo />
          </button>
        </div>
        <div className={style.topRow}>
          <div className={style.avatarContainer}>
            {client.avatarUrl ? (
              <img
                src={client.avatarUrl}
                alt={`${client.userName}'s avatar`}
                className={style.avatar}
              />
            ) : (
              <span
                className={style.avatarInitial}
                style={{ backgroundColor: avatarColor(client.userName || '') }}
              >
                {initialOf(client.userName || '')}
              </span>
            )}
          </div>
          {onDelete && (
            <button
              type="button"
              className={style.deleteButton}
              onClick={onDelete}
            >
              <DeleteLogo />
              Видалити
            </button>
          )}
        </div>
        <div className={style.details}>
          <SectionHeading icon={<PersonIcon />} title="Клієнт" />
          <div className={style.wrap}>
            <div className={style.warperInformation}>
              <h6>ПІБ КЛІЄНТА</h6>
              <p>{client.userName}</p>
            </div>
            <div className={style.warperInformation}>
              <h6>Email</h6>
              <p>{client.email}</p>
            </div>
            <div className={style.warperInformation}>
              <h6>Телефон</h6>
              <p>{client.phone}</p>
            </div>
          </div>
          <div className={style.wrap}>
            <div className={style.warperInformation}>
              <h6>Пошта підтверджена</h6>
              <p
                style={{
                  color: client.emailConfirmed ? '#0a8081' : '#ff3b30',
                }}
              >
                {client.emailConfirmed ? '✓ Так' : '✗ Ні'}
              </p>
            </div>
            <div className={style.warperInformation} style={{ flexGrow: 1 }}>
              <h6>Коментар</h6>
              {isEditingComment ? (
                <div className={style.commentEdit}>
                  <textarea
                    className={style.commentTextarea}
                    value={draftComment}
                    onChange={(e) => setDraftComment(e.target.value)}
                    placeholder="Додайте коментар…"
                    rows={2}
                    autoFocus
                  />
                  <div className={style.commentActions}>
                    <button
                      type="button"
                      className={style.commentSave}
                      onClick={saveComment}
                      disabled={savingComment}
                    >
                      {savingComment ? 'Збереження…' : 'Зберегти'}
                    </button>
                    <button
                      type="button"
                      className={style.commentCancel}
                      onClick={cancelEditComment}
                      disabled={savingComment}
                    >
                      Скасувати
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className={style.commentView}
                  onClick={startEditComment}
                  title="Натисніть, щоб редагувати"
                >
                  <span>{comment || '—'}</span>
                  <EditIcon />
                </p>
              )}
            </div>
          </div>
        </div>
        <div className={style.details}>
          <SectionHeading icon={<StarIcon />} title="Підписка" />
          <div className={style.wrap}>
            <div className={style.warperInformation}>
              <h6>Договір</h6>
              {hasCompany ? (
                <p
                  onClick={goToCompany}
                  title={`Договір ${client.contractNumber || '—'} · ${
                    client.companyName
                  }${
                    contractExpired ? ' (недійсний)' : ''
                  }. Перейти до картки компанії`}
                  style={{
                    color: contractExpired ? '#dc2626' : '#0a8081',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                  }}
                >
                  {client.contractNumber || '№ не вказано'}
                </p>
              ) : (
                <p>Без договору</p>
              )}
            </div>
            <div className={style.warperInformation}>
              <h6>Дата реєстрації</h6>
              <p>{formattedCreatedAt || '—'}</p>
            </div>
            <div className={style.warperInformation}>
              <h6>Останній вхід</h6>
              <p>{formattedLastLoginAt || '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetailsModal;
